import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import {
  ArticleDetail,
  ArticleSummary,
  Feed,
  Me,
  Settings,
  SummaryResponse,
} from './schemas';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => Me.parse(await api('/auth/me')),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useFeeds() {
  return useQuery({
    queryKey: ['feeds'],
    queryFn: async () => {
      const res = await api<{ feeds: unknown[] }>('/api/feeds');
      return Feed.array().parse(res.feeds);
    },
  });
}

export function useArticles(filter: 'unread' | 'starred' | 'all' | 'archived') {
  return useQuery({
    queryKey: ['articles', filter],
    queryFn: async () => {
      const res = await api<{ items: unknown[]; hasMore: boolean }>(
        `/api/articles?filter=${filter}`,
      );
      return {
        items: ArticleSummary.array().parse(res.items),
        hasMore: res.hasMore,
      };
    },
  });
}

export function useArticle(id: number | undefined) {
  return useQuery({
    queryKey: ['article', id],
    queryFn: async () => ArticleDetail.parse(await api(`/api/articles/${id}`)),
    enabled: id !== undefined,
  });
}

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => Settings.parse(await api('/api/settings')),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Settings>) =>
      Settings.parse(await api('/api/settings', { method: 'PUT', json: patch })),
    onSuccess: (data) => {
      qc.setQueryData(['settings'], data);
    },
  });
}

export function useArticleAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: number;
      action: 'read' | 'unread' | 'star' | 'unstar' | 'archive' | 'unarchive';
    }) => api(`/api/articles/${args.id}/${args.action}`, { method: 'POST' }),
    // Optimistic update so the toggle button flips instantly. If the upstream
    // call fails (e.g. Nextcloud returns 405 like older News versions did for
    // /items/:id/star), onError rolls the cache back so the user *sees* the
    // failure instead of a silent no-op.
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['article', vars.id] });
      const previous = qc.getQueryData(['article', vars.id]);
      qc.setQueryData(['article', vars.id], (old: unknown) => {
        const data = old as { starred?: boolean; unread?: boolean; archived?: boolean } | undefined;
        if (!data) return old;
        const patch: { starred?: boolean; unread?: boolean; archived?: boolean } = {};
        if (vars.action === 'star') patch.starred = true;
        if (vars.action === 'unstar') patch.starred = false;
        if (vars.action === 'read') patch.unread = false;
        if (vars.action === 'unread') patch.unread = true;
        if (vars.action === 'archive') {
          patch.archived = true;
          patch.starred = false; // archiving removes it from NC favorites too
        }
        if (vars.action === 'unarchive') patch.archived = false;
        return { ...data, ...patch };
      });
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(['article', vars.id], context.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: ['articles'] });
      void qc.invalidateQueries({ queryKey: ['article', vars.id] });
    },
  });
}

export function useSummary(id: number | undefined) {
  return useMutation({
    mutationFn: async () => SummaryResponse.parse(await api(`/api/articles/${id}/summary`, { method: 'POST' })),
  });
}
